//Ghost Users component
import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UserPresence } from '../../types';
import { GAME_CONFIG } from '../../utils/gameConfig';
import { getColorValue, getHashedColorForUser } from '../../utils/colors';

interface GhostUsersProps {
  ghostUsers: UserPresence[];
  viewportStartX: number;
  viewportStartY: number;
  viewportEndX: number;
  viewportEndY: number;
}

const GhostUsers: React.FC<GhostUsersProps> = ({
  ghostUsers,
  viewportStartX,
  viewportStartY,
  viewportEndX,
  viewportEndY
}) => {
  //Only show ghosts in viewport
  //ToDo: Record all ghost positions
  const visibleGhosts = ghostUsers.filter(user => 
    user.position.x >= viewportStartX && 
    user.position.x <= viewportEndX &&
    user.position.y >= viewportStartY && 
    user.position.y <= viewportEndY
  );

  //Group users by tile
  const userPositions = useMemo(() => {
    //Group users by position
    const usersByTile = new Map<string, UserPresence[]>();
    
    visibleGhosts.forEach(user => {
      const tileKey = `${user.position.x},${user.position.y}`;
      if (!usersByTile.has(tileKey)) {
        usersByTile.set(tileKey, []);
      }
      usersByTile.get(tileKey)!.push(user);
    });

    //Constant positioning by userID
    usersByTile.forEach(users => {
      users.sort((a, b) => a.userId.localeCompare(b.userId));
    });

    //Calculate individual positions
    return visibleGhosts.map(user => {
      const tileKey = `${user.position.x},${user.position.y}`;
      const usersOnTile = usersByTile.get(tileKey)!;
      const userIndex = usersOnTile.findIndex(u => u.userId === user.userId);
      
      //Base tile position
      const tileLeft = (user.position.x - viewportStartX) * GAME_CONFIG.TILE_SIZE;
      const tileTop = (user.position.y - viewportStartY) * GAME_CONFIG.TILE_SIZE;
      
      //Position based on user count
      let offsetX: number;
      let offsetY: number;
      
      if (usersOnTile.length === 1) {
        //Single user - center
        offsetX = 0.5;
        offsetY = 0.5;
      } else if (usersOnTile.length === 2) {
        //Two users - side by side
        offsetX = userIndex === 0 ? 0.3 : 0.7;
        offsetY = 0.5;
      } else if (usersOnTile.length === 3) {
        //Three users - triangle
        const positions = [
          { x: 0.5, y: 0.25 },  //Top center
          { x: 0.25, y: 0.75 }, //Bottom left
          { x: 0.75, y: 0.75 }  //Bottom right
        ];
        const pos = positions[userIndex] || positions[0];
        offsetX = pos.x;
        offsetY = pos.y;
      } else if (usersOnTile.length === 4) {
        //Four users - corners
        const positions = [
          { x: 0.25, y: 0.25 }, //Top left
          { x: 0.75, y: 0.25 }, //Top right
          { x: 0.25, y: 0.75 }, //Bottom left
          { x: 0.75, y: 0.75 }  //Bottom right
        ];
        const pos = positions[userIndex] || positions[0];
        offsetX = pos.x;
        offsetY = pos.y;
      } else {
        //5+ users - circular arrangement but my god i hope we never get here
        //NOTE: If this happens regularly, we may want to just.... redesign the whole game
        const angle = (userIndex / usersOnTile.length) * 2 * Math.PI;
        const radius = 0.3;
        offsetX = 0.5 + Math.cos(angle) * radius;
        offsetY = 0.5 + Math.sin(angle) * radius;
      }
      
      return {
        user,
        tileLeft,
        tileTop,
        offsetX,
        offsetY,
        userCount: usersOnTile.length,
        userIndex
      };
    });
  }, [visibleGhosts, viewportStartX, viewportStartY]);

  if (userPositions.length === 0) {
    return null;
  }

  return (
    <div className="absolute inset-0 pointer-events-none z-20">
      <AnimatePresence>
        {userPositions.map(({ user, tileLeft, tileTop, offsetX, offsetY, userCount, userIndex }) => {
          //Chosen color or fallback
          const userColor = getUserColor(user);
          
          return (
            <motion.div
              key={user.userId}
              className="absolute"
              style={{
                left: tileLeft,
                top: tileTop,
                width: GAME_CONFIG.TILE_SIZE,
                height: GAME_CONFIG.TILE_SIZE,
              }}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{ 
                type: "spring", 
                stiffness: 400, 
                damping: 25,
                duration: 0.3 
              }}
            >
              {/* User position within tile—CSS transform */}
              <div 
                className="absolute flex flex-col items-center"
                style={{
                  left: `${offsetX * 100}%`,
                  top: `${offsetY * 100}%`,
                  transform: 'translate(-50%, -50%)'
                }}
              >
                {/* Username - adaptive sizing */}
                <div className={`mb-1 px-1.5 py-0.5 bg-black/80 text-white rounded-md whitespace-nowrap shadow-lg border border-white/20 ${
                  userCount > 2 ? 'text-xs' : 'text-sm'
                }`}>
                  {userCount > 2 ? user.initials : user.username}
                </div>
                
                {/* Ghost dot w/color */}
                <div className="relative">
                  {/* Outer glow w/color */}
                  <div 
                    className={`absolute inset-0 rounded-full blur-sm animate-pulse ${
                      userCount > 3 ? 'w-3 h-3' : userCount > 1 ? 'w-4 h-4' : 'w-5 h-5'
                    }`}
                    style={{ backgroundColor: `${userColor}40` }} //25% opacity
                  />
                  
                  {/* Main dot w/color */}
                  <div 
                    className={`relative rounded-full border-2 shadow-md flex items-center justify-center ${
                      userCount > 3 ? 'w-3 h-3' : userCount > 1 ? 'w-4 h-4' : 'w-5 h-5'
                    }`}
                    style={{
                      backgroundColor: userColor,
                      borderColor: `${userColor}80`, //Slightly transparent border
                    }}
                  >
                    {/* Initials (larger dots) */}
                    {userCount <= 2 && (
                      <span className="text-white text-xs font-bold leading-none drop-shadow-sm">
                        {user.initials}
                      </span>
                    )}
                  </div>
                  
                  {/* Ripple effect */}
                  <motion.div
                    className={`absolute rounded-full border-2 ${
                      userCount > 3 ? 'w-4 h-4 -left-0.5 -top-0.5' : 
                      userCount > 1 ? 'w-5 h-5 -left-0.5 -top-0.5' : 
                      'w-6 h-6 -left-0.5 -top-0.5'
                    }`}
                    style={{ borderColor: `${userColor}60` }}
                    initial={{ scale: 0.8, opacity: 0.6 }}
                    animate={{ scale: 1.8, opacity: 0 }}
                    transition={{ 
                      duration: 1.5, 
                      repeat: Infinity,
                      ease: "easeOut" 
                    }}
                  />
                </div>
                
                {/* Activity indicator */}
                <div className={`mt-1 w-1 h-1 rounded-full ${getActivityColor(user.lastUpdate)}`} />
              </div>

              {/* User count indicator ToDo: is this necessary? */}
              {userCount > 4 && userIndex === 0 && (
                <div 
                  className="absolute top-1 right-1 w-4 h-4 text-white text-xs rounded-full flex items-center justify-center font-bold border border-white/30"
                  style={{ backgroundColor: userColor }}
                >
                  {userCount}
                </div>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>

      {/* Debug info */}
      <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs p-2 rounded">
        <div>Visible Ghosts: {userPositions.length}</div>
        <div>Crowded tiles: {
          Object.values(
            userPositions.reduce((acc, p) => {
              const key = `${p.user.position.x},${p.user.position.y}`;
              acc[key] = (acc[key] || 0) + 1;
              return acc;
            }, {} as Record<string, number>)
          ).filter(count => count > 1).length
        }</div>
      </div>
    </div>
  );
};

//Get color
const getUserColor = (user: UserPresence): string => {
  if (user.preferredColor) {
    return getColorValue(user.preferredColor);
  }
  
  //Fallback
  return getHashedColorForUser(user.userId);
};

//Get activity color based on last update
const getActivityColor = (lastUpdate: Date): string => {
  const timeDiff = Date.now() - lastUpdate.getTime();
  if (timeDiff < 3000) return 'bg-green-400';
  if (timeDiff < 30000) return 'bg-yellow-400';
  return 'bg-gray-400';
};

//Hash function for consistent colors
const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
};

export default GhostUsers;